import type { BlockOrderItem, BlockResponse, BlockUpdate, RuleInBlockUpdate } from '../api/types';

/**
 * Convert predicate response to predicate input format
 */
export const toPredicateInput = (predicate: { dsl: string | null; dslHuman: string | null } | null | undefined) => {
  if (!predicate) return undefined;
  return { dsl: predicate.dsl ?? '', dslHuman: predicate.dslHuman };
};

/**
 * Map rules from response format to update format
 */
export const mapRules = (rules: BlockResponse['rules']): RuleInBlockUpdate[] =>
  rules.map((rule) => ({
    id: rule.id,
    name: rule.name,
    text: rule.text,
    orderPosition: rule.orderPosition,
    isDeleted: rule.isDeleted,
    predicate: toPredicateInput(rule.predicate),
    knowledgeSetIds: rule.knowledgeSets.map((ks) => ks.id),
  }));

type BlockTransformer = (block: BlockResponse, parentBlockId: number | null) => Partial<BlockUpdate>;

/**
 * Flatten blocks tree into a flat array for the update API.
 * Optional transformer allows customizing individual block fields (e.g. marking as deleted, renaming).
 */
export const flattenBlocks = (
  blocks: BlockResponse[],
  parentBlockId: number | null = null,
  transformer?: BlockTransformer,
): BlockUpdate[] => {
  const result: BlockUpdate[] = [];
  for (const block of blocks) {
    const base: BlockUpdate = {
      id: block.id,
      parentBlockId,
      name: block.name,
      isHeader: block.isHeader,
      orderPosition: block.orderPosition,
      isDeleted: block.isDeleted,
      rules: mapRules(block.rules),
    };

    const overrides = transformer?.(block, parentBlockId);
    result.push(overrides ? { ...base, ...overrides } : base);

    if (block.children?.length) {
      result.push(...flattenBlocks(block.children, block.id, transformer));
    }
  }
  return result;
};

/**
 * Собирает плоский список позиций и родителей существующих неудалённых блоков для лёгкой
 * перестановки (PUT /expert-report/{id}/blocks-order). Удалённые блоки и их поддеревья
 * пропускаются — метод перестановки их не трогает. Позиция нормализуется в непрерывный
 * 0-based ряд среди активных соседей одного родителя, повторяя видимый порядок дерева.
 */
export const flattenBlocksOrder = (blocks: BlockResponse[], parentBlockId: number | null = null): BlockOrderItem[] => {
  const result: BlockOrderItem[] = [];
  const active = blocks.filter((block) => !block.isDeleted).sort((a, b) => a.orderPosition - b.orderPosition);
  active.forEach((block, index) => {
    result.push({ id: block.id, parentBlockId, orderPosition: index });
    if (block.children?.length) {
      result.push(...flattenBlocksOrder(block.children, block.id));
    }
  });
  return result;
};
